/**
 * QM8D — Zentraler Axios-Client mit Hybrid-Auth (Cookie + Bearer Fallback)
 * ADR-001: IMPLEMENTED — HttpOnly Secure Cookies + In-Memory Bearer Fallback
 *
 * SAME-ORIGIN FIX (v2): Custom-Domain-Deployments (z.B. qm-ki-8d.com) erkennen
 * automatisch, dass REACT_APP_BACKEND_URL auf die Preview-Domain zeigt und
 * nutzen stattdessen relative URLs (/api). Dadurch werden API-Calls Same-Origin
 * und HttpOnly Cookies werden korrekt vom Browser transportiert.
 *
 * Verantwortlichkeiten:
 * - API URL Konstante (Smart Origin Detection)
 * - withCredentials=true (Cookies werden automatisch gesendet)
 * - In-Memory Token-Speicher (nicht persistent, kein XSS-Risiko)
 * - Axios Request-Interceptor (Accept-Language + Bearer-Header)
 * - Axios Response-Interceptor (401 Auto-Refresh, Race-Condition-sicher)
 */
import axios from 'axios';
import { notifyReadOnlyChange } from '../contexts/TenantReadOnlyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Smart API Base Detection:
 * - Preview-Umgebung: Browser-Origin === BACKEND_URL → volle URL verwenden
 * - Custom-Domain-Deployment: Browser-Origin !== BACKEND_URL → relative /api
 *   Kubernetes-Ingress routet /api/* zum Backend auf ALLEN Domains.
 */
function _getApiBase() {
  if (!BACKEND_URL) return '/api';
  try {
    const backendOrigin = new URL(BACKEND_URL).origin;
    if (backendOrigin === window.location.origin) {
      return `${BACKEND_URL}/api`;
    }
    return '/api';
  } catch {
    return '/api';
  }
}

export const API = _getApiBase();

// Cookie-Auth: HttpOnly Cookies werden mitgesendet (ADR-001 IMPLEMENTED)
axios.defaults.withCredentials = true;

// In-Memory Token-Speicher (Cross-Origin Fallback)
let _memoryAccessToken = null;
let _memoryRefreshToken = null;

export function setMemoryTokens(accessToken, refreshToken) {
  _memoryAccessToken = accessToken || null;
  _memoryRefreshToken = refreshToken || null;
}

export function clearMemoryTokens() {
  _memoryAccessToken = null;
  _memoryRefreshToken = null;
}

// Accept-Language + Bearer-Header + CSRF-Token bei jedem Request mitsenden
function _getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

const _CSRF_UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

axios.interceptors.request.use((config) => {
  const lang = localStorage.getItem('qm8d_language') || 'de';
  config.headers['Accept-Language'] = lang;
  // X-Language: explizite Priorität für Backend-LocaleMiddleware (Phase 2 Multilingual)
  config.headers['X-Language'] = lang;
  // Bearer-Header als Fallback fuer Cross-Origin (wenn Cookie nicht greift)
  if (_memoryAccessToken && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${_memoryAccessToken}`;
  }
  // CSRF-Token (Double-Submit Cookie Pattern) fuer state-changing Requests
  if (_CSRF_UNSAFE_METHODS.has((config.method || '').toLowerCase())) {
    const csrfToken = _getCookie('csrf_token');
    if (csrfToken && !config.headers['X-CSRF-Token']) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
});

// 401 Auto-Refresh Interceptor — Race-Condition-sicher
let isRefreshing = false;
let refreshPromise = null;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

async function doRefresh() {
  // Cookie wird automatisch mitgesendet + Bearer als Fallback
  const config = { _skipAuthRetry: true };
  const body = {};
  // Refresh-Token im Body mitsenden falls Memory vorhanden (Cross-Origin Fallback)
  if (_memoryRefreshToken) {
    body.refresh_token = _memoryRefreshToken;
  }
  const res = await axios.post(`${API}/auth/refresh`, body, config);
  // Neue Tokens aus Response in Memory aktualisieren
  if (res.data) {
    if (res.data.token) _memoryAccessToken = res.data.token;
    if (res.data.refresh_token) _memoryRefreshToken = res.data.refresh_token;
  }
}

axios.interceptors.response.use(
  (response) => {
    // FIX-02 (QM8D Rev.1000): X-Tenant-ReadOnly Header auswerten
    try {
      const headers = response?.headers || {};
      const flag = headers['x-tenant-readonly'] || headers['X-Tenant-ReadOnly'];
      if (flag !== undefined) {
        const isReadOnly = String(flag).toLowerCase() === 'true';
        notifyReadOnlyChange({
          readOnly: isReadOnly,
          source: 'response_header',
          lastSeenAt: new Date().toISOString(),
        });
      }
    } catch {
      /* never fail axios pipeline due to interceptor */
    }
    return response;
  },
  async (error) => {
    // FIX-02 (QM8D Rev.1000): 503 mit X-Tenant-ReadOnly = aktive ReadOnly-Sperre
    try {
      const headers = error?.response?.headers || {};
      const flag = headers['x-tenant-readonly'] || headers['X-Tenant-ReadOnly'];
      if (flag !== undefined) {
        notifyReadOnlyChange({
          readOnly: String(flag).toLowerCase() === 'true',
          source: 'response_header_error',
          lastSeenAt: new Date().toISOString(),
        });
      } else if (error?.response?.status === 503) {
        const code = error?.response?.data?.error?.code;
        if (code === 'TENANT_READ_ONLY_MODE') {
          notifyReadOnlyChange({
            readOnly: true,
            source: 'response_503',
            lastSeenAt: new Date().toISOString(),
          });
        }
      }
    } catch {
      /* never fail */
    }

    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry && !originalRequest._skipAuthRetry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axios(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        refreshPromise = doRefresh();
        await refreshPromise;
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        clearMemoryTokens();
        const publicPaths = ['/supplier/respond/', '/ticket/', '/team-task/', '/report-render/', '/datenschutz', '/impressum', '/hilfe'];
        const isPublicRoute = publicPaths.some(p => window.location.pathname.startsWith(p));
        if (!isPublicRoute && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    }

    return Promise.reject(error);
  }
);

// ─── Helper-API (Nachfolger von utils/apiClient.js) ───────────────────
// Bietet eine kompakte Signatur für Komponenten, die nur einfache
// GET/POST/PUT/PATCH/DELETE gegen /api/* brauchen. Nutzt denselben
// globalen axios-Instance mit allen Interceptors (Cookie + Bearer-Fallback).
//
// Rückgabe: unwrapped ``response.data`` (nicht die ganze AxiosResponse) —
// kompatibel zur Signatur der früheren utils/apiClient.js-Facade.
const _buildUrl = (endpoint) => {
  const path = endpoint?.startsWith('/') ? endpoint : `/${endpoint || ''}`;
  return `${API}${path}`;
};

export const api = {
  async get(endpoint, config) {
    const res = await axios.get(_buildUrl(endpoint), config);
    return res.data;
  },
  async post(endpoint, data, config) {
    const res = await axios.post(_buildUrl(endpoint), data, config);
    return res.data;
  },
  async put(endpoint, data, config) {
    const res = await axios.put(_buildUrl(endpoint), data, config);
    return res.data;
  },
  async patch(endpoint, data, config) {
    const res = await axios.patch(_buildUrl(endpoint), data, config);
    return res.data;
  },
  async delete(endpoint, config) {
    const res = await axios.delete(_buildUrl(endpoint), config);
    return res.data;
  },
  baseUrl: API,
};
